import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Enums, log } from '@ohif/core';
import { Button } from '@ohif/ui-next';
import { URL_KEYS, getUrlParam } from '../utils/studyListFilterContract';

/**
 * Queries the data source for the study list and manages the result lifecycle:
 * refetch-on-filter-change, loading / first-fetch flags, and surfacing
 * connection errors (a modal with a Retry action). Pagination changes do not
 * trigger refetches — server batches are collected before paginating client-side.
 *
 * `refresh` invalidates the cached result and re-arms the first-fetch gate,
 * causing the next render to re-query. It also clears the loading flag, so the
 * caller can use it as the single reset when the active data source changes.
 */
export function useStudyListQuery({
  dataSource,
  isDataSourceInitialized,
  servicesManager,
}: {
  dataSource: any;
  isDataSourceInitialized: boolean;
  servicesManager: AppTypes.ServicesManager;
}): {
  studies: any[];
  isLoading: boolean;
  hasFetchedOnce: boolean;
  refresh: () => void;
} {
  const location = useLocation();
  const [studies, setStudies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [refreshId, setRefreshId] = useState(0);
  const studiesLimit = dataSource.getConfig?.()?.queryLimit ?? 101;
  // Keep equivalent filters stable, including modality order and page-only URL changes.
  const queryKey = JSON.stringify(_getQueryFilterValues(location.search, studiesLimit));
  const queryFilterValues = useMemo(() => JSON.parse(queryKey), [queryKey]);

  const refresh = useCallback(() => {
    setIsLoading(false);
    setHasFetchedOnce(false);
    setStudies([]);
    setRefreshId(id => id + 1);
  }, []);

  useEffect(() => {
    if (!isDataSourceInitialized) {
      return;
    }

    let cancelled = false;

    // 204: no content
    async function getData() {
      setIsLoading(true);
      log.time(Enums.TimingEnum.SEARCH_TO_LIST);
      try {
        const allStudies = [];
        const seenStudyUIDs = new Set<string>();
        let offset = 0;
        while (!cancelled) {
          const batch = await dataSource.query.studies.search({ ...queryFilterValues, offset });
          if (cancelled) {
            return;
          }
          if (!batch?.length) {
            break;
          }
          const previousCount = allStudies.length;
          for (const study of batch) {
            const uid = study.studyInstanceUid || study.StudyInstanceUID || JSON.stringify(study);
            if (!seenStudyUIDs.has(uid)) {
              seenStudyUIDs.add(uid);
              allStudies.push(study);
            }
          }
          // Sources that ignore offset repeat the same results; don't loop forever.
          if (allStudies.length === previousCount) {
            break;
          }
          // A server may cap batches below our requested limit. Continue until empty.
          offset += batch.length;
        }
        setStudies(allStudies);
        log.timeEnd(Enums.TimingEnum.SCRIPT_TO_VIEW);
        log.timeEnd(Enums.TimingEnum.SEARCH_TO_LIST);
      } catch (e) {
        if (cancelled) {
          return;
        }
        console.error(e);
        setStudies([]);

        // If there is a data source configuration API, the Worklist will pop
        // up its own dialog to attempt to configure it. Otherwise surface the
        // failure via a modal with a Retry action.
        const { configurationAPI, friendlyName } = dataSource.getConfig();
        if (!configurationAPI) {
          const { uiModalService } = servicesManager.services;
          uiModalService.show({
            title: 'Data Source Connection Error',
            content: () => (
              <div className="text-foreground">
                <p className="text-red-600">Error: {(e as Error).message}</p>
                <p>
                  Please ensure the following data source is configured correctly or is running:
                </p>
                <div className="mt-2 font-bold">{friendlyName}</div>
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={() => {
                      uiModalService.hide();
                      refresh();
                    }}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setHasFetchedOnce(true);
        }
      }
    }

    void getData();
    return () => {
      cancelled = true;
    };
  }, [queryFilterValues, dataSource, isDataSourceInitialized, servicesManager, refreshId, refresh]);

  return {
    studies,
    isLoading,
    hasFetchedOnce,
    refresh,
  };
}

/**
 * Translates the URL query string into the filter shape expected by the
 * data source (`patientId`, `patientName`, `modalitiesInStudy`, …).
 *
 * URL keys come from the centralized contract in `studyListFilterContract.ts`,
 * which is also what WorkList's URL serializer writes — so the read/write
 * sides can't drift.
 *
 * @param {*} query - URL search string or `URLSearchParams`
 */
function _getQueryFilterValues(query, queryLimit) {
  const params = new URLSearchParams(query);
  const modalities = getUrlParam(params, URL_KEYS.modalities);

  const queryFilterValues = {
    // DCM
    patientId: getUrlParam(params, URL_KEYS.mrn),
    patientName: getUrlParam(params, URL_KEYS.patientName),
    studyDescription: getUrlParam(params, URL_KEYS.description),
    modalitiesInStudy: modalities ? modalities.split(',').sort() : null,
    accessionNumber: getUrlParam(params, URL_KEYS.accession),
    //
    startDate: getUrlParam(params, URL_KEYS.startDate),
    endDate: getUrlParam(params, URL_KEYS.endDate),
    // Rarely supported server-side
    sortBy: getUrlParam(params, URL_KEYS.sortBy),
    sortDirection: getUrlParam(params, URL_KEYS.sortDirection),
    // queryLimit controls the batch size, not the total number of accessible studies.
    offset: 0,
    limit: queryLimit,
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}
